import { ItemDetail } from '../types/HistoricalReport';

export const enum ReportType {
  WeatherConditions = 'Weather Conditions',
  PolicyDelays = 'Policy Delays',
}

export const enum Granularity {
  FifteenMin = 15,
  ThirtyMin = 30,
  OneHour = 60,
  OneDay = 1440,
}

export const enum GranularityLabels {
  FifteenMin = '15 min',
  ThirtyMin = '30 min',
  OneHour = '60 min',
  OneDay = '24 hour',
}

export const OBSERVATIONS_DATA_INTERVAL: Record<string, Granularity> = {
  ['15 min']: Granularity.FifteenMin,
  ['30 min']: Granularity.ThirtyMin,
  ['60 min']: Granularity.OneHour,
  ['24 hour']: Granularity.OneDay,
};

export const OBSERVATIONS_DATA_INTERVAL_ARRAY = Object.entries(OBSERVATIONS_DATA_INTERVAL);

export const FORMATS = ['CSV', 'PDF'];

// const ORIGINS = [
//   'Location',
//   'Weather Station',
// ];

export const SUMAMARY_FREQUENCY = [
  { name: 'Overall', info: 'Shows combined alert totals and duration for the entire time period' },
  { name: 'Monthly', info: 'Breaks down alerts by month to show patterns over longer periods' },
  { name: 'Weekly', info: 'Groups alerts by week to identify weekly trends and patterns' },
  { name: 'Daily', info: 'Provides detailed day-by-day breakdown of all alerts' },
];

export const DEFAULT_MAX_CHIPS = 6;

export enum MetricType {
  Max = 'max',
  Min = 'min',
  Avg = 'avg',
  Sum = 'sum',
}

export const METRICS: Record<keyof typeof MetricType, ItemDetail> = {
  Max: { value: MetricType.Max, label: 'Maximum' },
  Min: { value: MetricType.Min, label: 'Minimum' },
  Avg: { value: MetricType.Avg, label: 'Average' },
  Sum: { value: MetricType.Sum, label: 'Accumulation' },
};

export const OBSERVATIONS = [
  {
    value: 'AmbientTemperature',
    label: 'Ambient Temperature',
    metrics: [METRICS['Avg'], METRICS['Max'], METRICS['Min']],
    minInterval: GranularityLabels.FifteenMin,
  },
  {
    value: 'FeelsLike',
    label: 'Feels Like',
    metrics: [METRICS['Avg'], METRICS['Max'], METRICS['Min']],
    minInterval: GranularityLabels.FifteenMin,
  },
  {
    value: 'HeatIndex',
    label: 'Heat Index',
    metrics: [METRICS['Avg'], METRICS['Max']],
    minInterval: GranularityLabels.FifteenMin,
  },
  {
    value: 'WindChill',
    label: 'Wind Chill',
    metrics: [METRICS['Avg'], METRICS['Min']],
    minInterval: GranularityLabels.FifteenMin,
  },
  {
    value: 'WBGT',
    label: 'WBGT',
    metrics: [METRICS['Avg'], METRICS['Max']],
    minInterval: GranularityLabels.FifteenMin,
  },
  {
    value: 'Precipitation',
    label: 'Precipitation',
    metrics: [METRICS['Sum']],
    minInterval: GranularityLabels.FifteenMin,
  },
  {
    value: 'RelativeHumidity',
    label: 'Relative Humidity',
    metrics: [METRICS['Avg'], METRICS['Max'], METRICS['Min']],
    minInterval: GranularityLabels.FifteenMin,
  },
  {
    value: 'DewPoint',
    label: 'Dew Point',
    metrics: [METRICS['Avg'], METRICS['Max'], METRICS['Min']],
    minInterval: GranularityLabels.FifteenMin,
  },
  {
    value: 'Evapotranspiration',
    label: 'Evapotranspiration',
    metrics: [METRICS['Sum']],
    minInterval: GranularityLabels.OneDay,
  },
  { value: 'WindGust', label: 'Wind Gust', metrics: [METRICS['Max']], minInterval: GranularityLabels.FifteenMin },
  {
    value: 'WindSpeed',
    label: 'Wind Speed',
    metrics: [METRICS['Avg'], METRICS['Max'], METRICS['Min']],
    minInterval: GranularityLabels.FifteenMin,
  },
  {
    value: 'PM2_5_AQI',
    label: 'AQI (PM2.5)',
    metrics: [METRICS['Avg'], METRICS['Max'], METRICS['Min']],
    minInterval: GranularityLabels.FifteenMin,
  },
  {
    value: 'PM10_AQI',
    label: 'AQI (PM10)',
    metrics: [METRICS['Avg'], METRICS['Max'], METRICS['Min']],
    minInterval: GranularityLabels.FifteenMin,
  },
];

// TODO: improve abbreviation mapper on toggle select component
export const ABBREVIATIONS = new Map<string, string>([
  ['average', 'avg'],
  ['maximum', 'max'],
  ['minimum', 'min'],
  ['accumulation', 'sum'],
  ['temperature (temp)', 'temp'],
  ['heat index', 'heat'],
  ['wet bulb globe temperature (wbgt)', 'WBGT'],
  ['wind chill', 'chill'],
  ['wind speed (speed)', 'speed'],
  ['wind gust (gust)', 'gust'],
  ['precipitation (total)', 'precip'],
  ['air quality index (aqi max)', 'max'],
  ['air quality index (pm2.5)', 'pm2.5'],
  ['air quality index (pm10)', 'pm10'],
  ['air quality index (ozone)', 'O3'],
]);

export const MAX_DAYS_BY_GRANULARITY: Record<Granularity, number> = {
  [Granularity.FifteenMin]: 14,
  [Granularity.ThirtyMin]: 31,
  [Granularity.OneHour]: 92,
  [Granularity.OneDay]: 365 * 5,
};

export enum PolicyType {
  Temp = 'temp',
  HeatIndex = 'heatIndex',
  WBGT = 'WBGT',
  WindChill = 'windChill',
  WindSpeed = 'windSpeed',
  Gust = 'gust',
  Precip = 'precip',
  AQIMax = 'AQIMax',
  PM25 = 'PM25',
  PM10 = 'PM10',
  Ozone = 'ozonne',
}

export const POLICY_CATEGORIES: Record<keyof typeof PolicyType, ItemDetail> = {
  Temp: { value: PolicyType.Temp, label: 'Temperature (Temp)' },
  HeatIndex: { value: PolicyType.HeatIndex, label: 'Heat Index' },
  WBGT: { value: PolicyType.WBGT, label: 'Wet Bulb Globe Temperature (WBGT)' },
  WindChill: { value: PolicyType.WindChill, label: 'Wind Chill' },
  WindSpeed: { value: PolicyType.WindSpeed, label: 'Wind Speed (Speed)' },
  Gust: { value: PolicyType.Gust, label: 'Wind Gust (Gust)' },
  Precip: { value: PolicyType.AQIMax, label: 'Precipitation (Total)' },
  AQIMax: { value: PolicyType.AQIMax, label: 'Air Quality Index (AQI Max)' },
  PM25: { value: PolicyType.PM25, label: 'Air Quality Index (PM2.5)' },
  PM10: { value: PolicyType.PM10, label: 'Air Quality Index (PM10)' },
  Ozone: { value: PolicyType.Ozone, label: 'Air Quality Index (Ozone)' },
};

export const POLICIES = [
  {
    value: 'Lightning',
    label: 'Lightning',
  },
  {
    value: 'HeatStress',
    label: 'Heat Stress',
    types: [POLICY_CATEGORIES['Temp'], POLICY_CATEGORIES['HeatIndex'], POLICY_CATEGORIES['WBGT']],
  },
  {
    value: 'ColdStress',
    label: 'Cold Stress',
    types: [POLICY_CATEGORIES['Temp'], POLICY_CATEGORIES['WindChill']],
  },
  {
    value: 'Wind',
    label: 'Wind',
    types: [POLICY_CATEGORIES['WindSpeed'], POLICY_CATEGORIES['Gust']],
  },
  {
    value: 'Precipitation',
    label: 'Precipitation',
    types: [POLICY_CATEGORIES['Precip'], POLICY_CATEGORIES['Gust']],
  },
  {
    value: 'Air Quality',
    label: 'Air Quality',
    types: [
      POLICY_CATEGORIES['AQIMax'],
      POLICY_CATEGORIES['PM25'],
      POLICY_CATEGORIES['PM10'],
      POLICY_CATEGORIES['Ozone'],
    ],
  },
  // For later...
  // {
  //   value: 'Humidity',
  //   label: 'Humidity',
  //   types: ['Relative Humidity (Humidity)', 'Dew Point (Dew)'],
  // },
  // {
  //   value: 'Precipitation',
  //   label: 'Precipitation',
  //   types: ['Rainfall Total (Total)', 'Rainfall Rate (Rate)'],
  // },
];

// TODO: Update policy intervals with any necesary restrictions
export const POLICY_DATA_INTERVAL: Record<string, Granularity> = {
  ['Detailed']: Granularity.FifteenMin,
  ['Daily']: Granularity.ThirtyMin,
  ['Weekly']: Granularity.OneHour,
  ['Monthly']: Granularity.OneDay,
  ['Overall Summary']: Granularity.OneDay,
};

export const POLICY_DATA_INTERVAL_ARRAY = Object.entries(POLICY_DATA_INTERVAL);
